/**
 * Pressure-relief handlers registered with the memory governor.
 *
 * Each handler frees memory in a specific subsystem. They run in priority
 * order at the `critical` level (lowest priority first, i.e. least
 * disruptive). The governor stops the chain as soon as a post-handler
 * sample drops the level below critical, so heavy levers (`trim-undo-history`)
 * only fire when lighter ones haven't relieved enough pressure.
 *
 * Adding a new lever: append a `registerPressureHandler({...})` call below
 * (or invoke it from the owning subsystem's init code if the lever lives in
 * a different module). No changes to the governor are required.
 */

import { cancelAllPendingAsyncBoundsTrees } from "../viewport/asyncBoundsTree.ts";
import { clearModifierCache } from "../viewport/modifierApply.ts";
import { clearMaterialLibraryCache } from "./materialLibraryCache.ts";
import { registerPressureHandler } from "./memoryGovernor.ts";
import { useStore } from "./store.ts";
import { undoManager } from "./storeUndo.ts";

const MB = 1024 * 1024;

/**
 * Wire the default handlers into the governor. Idempotent — calling more
 * than once will register duplicate handlers (don't). Call once from the
 * app boot path next to `startMemoryGovernor()`.
 *
 * Returns a disposer that unregisters every handler this call installed.
 */
export function registerDefaultPressureHandlers(): () => void {
  const disposers: Array<() => void> = [];

  // p10 — Modifier cache. Transparent: re-evaluates on next render.
  disposers.push(
    registerPressureHandler({
      id: "clear-modifier-cache",
      priority: 10,
      description:
        "Drop cached modifier-stack outputs; modifiers re-evaluate on demand.",
      run: () => {
        clearModifierCache();
        // The cache is opaque (lives in the modifier worker) — we report 0
        // since we cannot accurately measure freed bytes from the main thread.
        return { freedBytes: 0 };
      },
    }),
  );

  // p20 — Material library cache. Tiny but free.
  disposers.push(
    registerPressureHandler({
      id: "clear-material-library",
      priority: 20,
      description:
        "Drop in-memory library material JSONs; refetched on next access.",
      run: () => {
        clearMaterialLibraryCache();
        return { freedBytes: 0 };
      },
    }),
  );

  // p25 — Pending BVH builds. Cheap to cancel; rebuilds on next pick.
  disposers.push(
    registerPressureHandler({
      id: "cancel-pending-bvh",
      priority: 25,
      description:
        "Cancel queued BVH acceleration builds; rebuild on next interaction.",
      run: () => {
        const cleared = cancelAllPendingAsyncBoundsTrees();
        // Best-effort estimate: each queued job retains a captured typed-array
        // input proportional to its geometry. We don't have an exact size, so
        // attribute a rough 1 MB per cleared job — informational only.
        return { freedBytes: cleared * MB };
      },
    }),
  );

  // p30 — Undo history. Evict the oldest half of retained snapshot bytes,
  // then immediately restore the user's configured cap so future operations
  // can grow back to the chosen ceiling. **Monotonic**: the evicted entries
  // themselves do not return — the user loses oldest-first undo depth, but
  // their setting is untouched.
  disposers.push(
    registerPressureHandler({
      id: "trim-undo-history",
      priority: 30,
      description: "Evict oldest undo entries to free retained snapshot bytes.",
      run: () => {
        const before = undoManager.bytesUsed;
        const userBudget = useStore.getState().undoMemoryBudgetBytes;
        const trimTarget = Math.max(1, Math.floor(before / 2));
        // First call evicts down to the trim target; second call restores
        // the user's configured cap (no-op on entries — they're already
        // gone — but it lets future pushes grow back to the chosen budget).
        undoManager.setMaxBytes(trimTarget);
        undoManager.setMaxBytes(userBudget);
        return { freedBytes: Math.max(0, before - undoManager.bytesUsed) };
      },
    }),
  );

  return () => {
    for (const d of disposers) d();
    disposers.length = 0;
  };
}
